import type {
  LookupResponse,
  LoginResponse,
  User,
  AuthProviderInfo,
  UserRecord,
  TestProviderResponse,
} from "./types";
import { logError } from "@hub/shared/utils/errorLogger";
import { getAccessToken } from "./client";

const API_BASE = "/api";

function getCsrfToken(): string | null {
  const match = document.cookie.match(/csrf_token=([^;]+)/);
  return match ? match[1] : null;
}

// Returns CSRF headers, or throws if the token is missing.
// Use for all state-mutating requests that require an authenticated session.
function csrfHeaders(): Record<string, string> {
  const token = getCsrfToken();
  if (!token) {
    throw new Error("[authService] CSRF token is missing — refusing to send state-mutating request without CSRF protection");
  }
  return { "X-CSRF-Token": token };
}

// Returns CSRF headers without throwing when the token is absent.
// Only for bootstrap endpoints (lookup/login) where the CSRF cookie may not
// yet be present on the client before the server sets it on the first response.
function csrfHeadersOptional(): Record<string, string> {
  const token = getCsrfToken();
  if (!token) {
    console.warn("[authService] CSRF token not found on pre-auth request — request proceeding without CSRF header");
  }
  return token ? { "X-CSRF-Token": token } : {};
}

// Returns headers for authenticated REST calls, including the in-memory
// access token as a Bearer token and CSRF protection for mutating requests.
function authHeaders(): Record<string, string> {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...csrfHeaders(),
  };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  return headers;
}

// Returns auth headers for GET requests (no CSRF required, no Content-Type).
function authReadHeaders(): Record<string, string> {
  const token = getAccessToken();
  if (!token) return {};
  return { "Authorization": `Bearer ${token}` };
}

export const authService = {
  async lookup(email: string): Promise<LookupResponse> {
    const response = await fetch(`${API_BASE}/auth/lookup`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeadersOptional() },
      body: JSON.stringify({ email }),
    });
    if (!response.ok) throw new Error("Lookup failed");
    return response.json();
  },

  async login(email: string, password: string): Promise<LoginResponse> {
    const response = await fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json", ...csrfHeadersOptional() },
      body: JSON.stringify({ email, password }),
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Login failed");
    }
    return response.json();
  },

  async logout(): Promise<void> {
    await fetch(`${API_BASE}/auth/logout`, {
      method: "POST",
      headers: { ...csrfHeaders() },
      credentials: "include",
    });
  },

  async refresh(): Promise<{ user: User | null; accessToken?: string; networkError?: boolean }> {
    try {
      const response = await fetch(`${API_BASE}/auth/refresh`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...csrfHeadersOptional(),
        },
      });

      if (!response.ok) {
        // Server responded — this is a real auth failure
        return { user: null };
      }

      const data = await response.json();
      return {
        user: data.user || null,
        accessToken: data.access_token,
      };
    } catch (err) {
      // Network error — don't treat as auth failure
      logError("authService.refresh", err);
      return { user: null, networkError: true };
    }
  },

  async getProviders(): Promise<AuthProviderInfo[]> {
    const response = await fetch(`${API_BASE}/auth/providers`, {
      headers: { ...authReadHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to fetch providers");
    const data = await response.json();
    return data.providers;
  },

  async getUsers(): Promise<UserRecord[]> {
    const response = await fetch(`${API_BASE}/users`, {
      headers: { ...authReadHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to fetch users");
    const data = await response.json();
    return data.users;
  },

  async createUser(
    user: Partial<UserRecord> & { password?: string }
  ): Promise<UserRecord> {
    const response = await fetch(`${API_BASE}/users`, {
      method: "POST",
      headers: { ...authHeaders() },
      body: JSON.stringify(user),
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to create user");
    const data = await response.json();
    return data.user;
  },

  async deleteUser(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/users/${id}`, {
      method: "DELETE",
      headers: { ...authHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to delete user");
  },

  // Auth Provider Management
  async listProviders(includeDisabled = false): Promise<AuthProviderInfo[]> {
    const response = await fetch(
      `${API_BASE}/auth/providers?includeDisabled=${includeDisabled}`,
      { headers: { ...authReadHeaders() }, credentials: "include" }
    );
    if (!response.ok) throw new Error("Failed to fetch providers");
    const data = await response.json();
    return data.providers;
  },

  async getProvider(id: string): Promise<AuthProviderInfo> {
    const response = await fetch(`${API_BASE}/auth/providers/${id}`, {
      headers: { ...authReadHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to fetch provider");
    const data = await response.json();
    return data.provider;
  },

  async createProvider(provider: Partial<AuthProviderInfo>): Promise<AuthProviderInfo> {
    const response = await fetch(`${API_BASE}/auth/providers`, {
      method: "POST",
      headers: { ...authHeaders() },
      body: JSON.stringify({ provider }),
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to create provider");
    }
    const data = await response.json();
    return data.provider;
  },

  async updateProvider(provider: AuthProviderInfo): Promise<AuthProviderInfo> {
    const response = await fetch(`${API_BASE}/auth/providers/${provider.id}`, {
      method: "PUT",
      headers: { ...authHeaders() },
      body: JSON.stringify({ provider }),
      credentials: "include",
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || "Failed to update provider");
    }
    const data = await response.json();
    return data.provider;
  },

  async deleteProvider(id: string): Promise<void> {
    const response = await fetch(`${API_BASE}/auth/providers/${id}`, {
      method: "DELETE",
      headers: { ...authHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to delete provider");
  },

  async testProvider(id: string): Promise<TestProviderResponse> {
    const response = await fetch(`${API_BASE}/auth/providers/${id}/test`, {
      method: "POST",
      headers: { ...authHeaders() },
      credentials: "include",
    });
    if (!response.ok) throw new Error("Failed to test provider");
    return response.json();
  },
};
